import React from "react";
import { COLORS } from "../chartTheme";

const mono = "'JetBrains Mono',monospace";

function PageWrapper({ title, subtitle, children }) {
  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      <div style={{ marginBottom: 30 }}>
        <div style={{ fontSize: 10, color: COLORS.primary, fontFamily: mono, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>{subtitle}</div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text }}>{title}</h1>
      </div>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>{title}</h2>
      <div style={{ fontSize: 13, color: COLORS.textDim, lineHeight: 1.7 }}>{children}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PRIVACY POLICY
// ═══════════════════════════════════════════════════════════

export function PrivacyPolicy() {
  return (
    <PageWrapper title="Privacy Policy" subtitle="Legal">
      <div style={{ fontSize: 10, color: COLORS.muted, fontFamily: mono, marginBottom: 20 }}>
        Effective: April 1, 2026 | Last updated: April 8, 2026
      </div>

      <Section title="Overview">
        Pathriva Inc. provides clinical outcomes analytics for GLP-1 telehealth operators.
        We process aggregate health data on behalf of our customers (Covered Entities under HIPAA).
        This policy describes how we handle data in our role as a Business Associate.
      </Section>

      <Section title="Data We Process">
        We process de-identified and aggregated clinical data including: patient demographics (age range, gender),
        medication records (GLP-1 prescriptions, dosages), clinical observations (weight, A1C, blood pressure),
        and adherence metrics (PDC calculations). We do NOT store: Social Security numbers, financial data,
        unencrypted patient names outside of FHIR-standard identifiers, or direct patient contact information.
      </Section>

      <Section title="HIPAA Compliance">
        Pathriva operates as a Business Associate under HIPAA. We execute a Business Associate Agreement (BAA)
        with every customer before any data flows. All patient data is processed within Google Cloud Platform
        with a signed GCP BAA. PHI is never sent to external AI APIs or third-party analytics services.
      </Section>

      <Section title="Data Security">
        All data is encrypted at rest using AES-256 via Google Cloud's default encryption. Data in transit
        is protected by TLS 1.2+. Access to patient data is controlled by JWT-based multi-tenant isolation —
        each customer's data is logically separated and scoped by customer_id in every query.
        We maintain HIPAA-compliant audit logs of all data access.
      </Section>

      <Section title="Data Retention & Deletion">
        Customer data is retained for the duration of the service agreement. Upon contract termination,
        all customer data is purged within 30 days. Certified destruction confirmation is provided on request.
        HIPAA-required audit logs are retained for 6 years per federal requirements.
      </Section>

      <Section title="Your Rights">
        Our customers' patients may exercise their HIPAA rights (access, amendment, accounting of disclosures)
        through their healthcare provider. We support our customers in fulfilling these requests within
        the required timeframes.
      </Section>

      <Section title="Contact">
        Privacy: privacy@pathriva.com | Security: security@pathriva.com | General: business@pathriva.com
      </Section>
    </PageWrapper>
  );
}

// ═══════════════════════════════════════════════════════════
// SECURITY PAGE
// ═══════════════════════════════════════════════════════════

export function SecurityPage() {
  const items = [
    { cat: "Infrastructure", details: [
      "Google Cloud Platform with signed BAA",
      "BigQuery with AES-256 encryption at rest",
      "Cloud Run (serverless) — no persistent VMs to patch",
      "US-only data residency",
      "Cloudflare WAF for edge protection",
    ]},
    { cat: "Authentication & Access", details: [
      "JWT-based authentication with 8-hour token expiry",
      "Multi-tenant isolation — customer_id from JWT only",
      "Role-based access control: super_admin, admin, operator, viewer",
      "PBKDF2-HMAC-SHA256 password hashing (600K iterations, OWASP 2023)",
      "Constant-time password comparison (timing attack prevention)",
    ]},
    { cat: "Data Protection", details: [
      "AES-256 encryption at rest, TLS 1.2+ in transit",
      "All BigQuery queries parameterized — SQL injection impossible",
      "PHI never sent to external AI APIs",
      "No PHI stored in browser (sessionStorage only, cleared on tab close)",
      "Login responses never reveal whether email exists",
    ]},
    { cat: "Monitoring & Compliance", details: [
      "64 SOC 2 controls monitored continuously (Trust Protocol)",
      "Current score: A+ (100%)",
      "Every data access logged with timestamp, user, tenant, IP",
      "Audit logs retained 6 years per HIPAA",
      "210 automated tests covering auth, isolation, and security",
      "Rate limiting: 100 req/min default, 10/min on auth endpoints",
    ]},
    { cat: "Compliance Status", details: [
      "HIPAA Business Associate Agreement: Signed",
      "GCP BAA: Signed (April 2026)",
      "SOC 2 Type I: In preparation (target Q3 2026)",
      "Trust Protocol: A+ — 64/64 controls passing",
    ]},
  ];

  return (
    <PageWrapper title="Security" subtitle="Trust Protocol">
      <div style={{ fontSize: 13, color: COLORS.textDim, lineHeight: 1.7, marginBottom: 24 }}>
        Pathriva is built for HIPAA-regulated healthcare data from the ground up.
        Security is continuously monitored via our Trust Protocol — 64 SOC 2 controls checked automatically.
      </div>

      {items.map(s => (
        <div key={s.cat} style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>{s.cat}</h3>
          {s.details.map((d, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 5 }}>
              <span style={{ color: COLORS.primary, fontSize: 10, marginTop: 4 }}>●</span>
              <span style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.5 }}>{d}</span>
            </div>
          ))}
        </div>
      ))}

      <div style={{
        marginTop: 20, padding: 14, borderRadius: 8,
        background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.15)",
        fontSize: 12, color: COLORS.muted,
      }}>
        Questions? security@pathriva.com | Responsible disclosure: report vulnerabilities to security@pathriva.com
      </div>
    </PageWrapper>
  );
}

// ═══════════════════════════════════════════════════════════
// INSIGHTS / BLOG
// ═══════════════════════════════════════════════════════════

const ARTICLES = [
  {
    id: "glp1-dropout-crisis",
    title: "The GLP-1 Dropout Crisis: 50-70% of Patients Quit Within a Year",
    date: "Apr 3, 2026",
    category: "Clinical Outcomes",
    readTime: "5 min",
    summary: "Most GLP-1 programs don't track attrition until it's too late. Here's why early prediction changes everything.",
    content: `Between 50% and 70% of patients prescribed GLP-1 receptor agonists discontinue therapy within 12 months. For telehealth operators managing thousands of patients, this isn't just a clinical concern — it's an existential business risk.

The challenge isn't that patients quit. It's that operators don't see it coming. Most programs rely on retrospective analysis rather than prospective prediction.

Our data shows that the highest-risk period for discontinuation is days 30-90 of therapy. This is when initial side effects peak, insurance friction surfaces, and patients who don't see early weight response lose motivation.

With real-time PDC tracking and a five-factor attrition model, operators can identify at-risk patients 30-45 days before they discontinue. A phone call at day 45 costs $15. Losing that patient costs $4,200 in annual savings that never materialize.`,
  },
  {
    id: "pdc-currency",
    title: "PDC Is Becoming the Currency of GLP-1 Outcomes",
    date: "Mar 28, 2026",
    category: "Adherence",
    readTime: "4 min",
    summary: "Proportion of Days Covered isn't just a pharmacy metric anymore. It's how payers will measure your program.",
    content: `PDC (Proportion of Days Covered) has been the gold standard for medication adherence measurement in pharmacy for decades. Now it's crossing over into GLP-1 program evaluation.

PDC = total days supply dispensed / observation period × 100. A patient with PDC ≥ 80% is considered adherent per CMS standards.

Payers are moving from "did you prescribe it?" to "did the patient take it?" The upcoming CMS BALANCE Model ties Medicare GLP-1 coverage directly to documented outcomes. PDC will be a primary metric.

The operators who win will be the ones who don't just report PDC — but act on it.`,
  },
  {
    id: "employer-roi",
    title: "The $12K Question: How Employers Can Prove GLP-1 ROI",
    date: "Mar 21, 2026",
    category: "ROI",
    readTime: "6 min",
    summary: "Employers are spending $12,000+ per year per GLP-1 patient. Benefits directors need proof it's working.",
    content: `The average employer spends $12,000-$15,000 per year per employee on GLP-1 therapy. With 20% of large employers now covering these medications, CFOs are asking: is this worth it?

An adherent GLP-1 patient saves an estimated $4,200 per year in reduced ER visits, hospitalizations, and comorbidity management. But that's only for adherent patients.

Benefits directors need three things: population-level adherence data, clinical outcomes with confidence intervals, and cost-effectiveness calculations they can present to their board.

If you're the operator who can hand an employer a real-time evidence packet showing "$2.1M in documented savings across your 500 covered employees," you don't just retain the contract. You expand it.`,
  },
];

export function InsightsPage() {
  const [selectedArticle, setSelectedArticle] = React.useState(null);

  if (selectedArticle) {
    const article = ARTICLES.find(a => a.id === selectedArticle);
    return (
      <PageWrapper title={article.title} subtitle={article.category}>
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          <span style={{ fontSize: 10, color: COLORS.muted, fontFamily: mono }}>{article.date}</span>
          <span style={{ fontSize: 10, color: COLORS.muted, fontFamily: mono }}>{article.readTime}</span>
        </div>
        <div style={{ fontSize: 14, color: COLORS.textDim, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
          {article.content}
        </div>
        <button onClick={() => setSelectedArticle(null)} style={{
          marginTop: 20, background: COLORS.bg, border: `1px solid ${COLORS.border}`,
          borderRadius: 6, color: COLORS.muted, padding: "8px 16px", fontSize: 12,
          cursor: "pointer", fontFamily: "inherit",
        }}>Back to articles</button>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper title="Insights" subtitle="Blog">
      <div style={{ fontSize: 13, color: COLORS.textDim, marginBottom: 24 }}>
        Perspectives on GLP-1 outcomes tracking, clinical analytics, and healthcare data.
      </div>
      {ARTICLES.map(a => (
        <div key={a.id} onClick={() => setSelectedArticle(a.id)} style={{
          background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10,
          padding: 18, marginBottom: 12, cursor: "pointer", transition: "border-color 0.15s",
        }} onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(0,229,255,0.3)"}
           onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 6, background: "rgba(96,165,250,0.12)", color: "#60a5fa", fontFamily: mono }}>{a.category}</span>
            <span style={{ fontSize: 9, color: COLORS.muted, fontFamily: mono }}>{a.readTime}</span>
            <span style={{ fontSize: 9, color: COLORS.muted, fontFamily: mono, marginLeft: "auto" }}>{a.date}</span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>{a.title}</div>
          <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.5 }}>{a.summary}</div>
        </div>
      ))}
    </PageWrapper>
  );
}
